import i18next from "utilities/i18n";
import { format } from "date-fns";
import { DATE_FORMAT, UNASSIGNED } from "Constants";

const simpleCollator = new Intl.Collator();

export const handleNullFields = (
  value: string | null | undefined,
  dash?: boolean
): string => {
  if (value?.trim() === "") {
    return i18next.t("NOT_SET");
  }

  return dash ? value ?? "-" : value ?? i18next.t("UNKNOWN");
};

export const handleNullDates = (
  stringRepresentation: string | null | undefined,
  dateFormat: string = DATE_FORMAT.US_SHORT
): string => {
  return stringRepresentation
    ? format(new Date(stringRepresentation), dateFormat)
    : handleNullFields(undefined);
};

export const slugify = (value: string) =>
  value.replace(/[^a-z0-9]/gi, "-").toLowerCase();

export function extractDomain(email: string): string {
  const at = email.lastIndexOf("@") + 1;
  if (at < 1) {
    return "";
  }
  const lastPeriod = email.lastIndexOf(".");
  const end = email.length;
  const endDomain = lastPeriod > at ? lastPeriod : end;
  const domainSize = endDomain - at;
  if (domainSize < 1) {
    return "";
  }
  return email.substr(at, domainSize);
}

export function extractFullDomain(email: string): string {
  const at = email.lastIndexOf("@") + 1;
  if (at < 1) {
    return "";
  }
  const end = email.length;
  const domainSize = end - at;
  if (domainSize < 1) {
    return "";
  }
  return email.substr(at, domainSize);
}

/**
 * Ideally, versions should be just numbers separated by dots.
 * But in some cases version contains build part in parenthesis (i.e. 1.0.0 (Build: 5484)).
 *
 * Returns version in format x.x.x.x (numbers separated by dots)
 *
 * @param version
 *
 * @return {string}
 */
export const formatVersion = (version: string | null) => {
  if (!version) {
    return version;
  }

  const [ver, build] = version.split(/\(build:/i).map(s => s.trim());

  return `${ver}${build ? `.${parseInt(build, 10)}` : ""}`;
};

/**
 * Compare version number strings.
 * Returns -1 (if first version is lower), 0 (if equal), or 1 (if first version is higher)
 * Lifted straight from semver-compare and slightly modified - why import a library for just one tiny function?
 *
 * @param a
 * @param b
 * @param compareBuild
 *
 * @return {number}
 */
export const compareVersion = (
  a: string | null,
  b: string,
  compareBuild: boolean = false
) => {
  // support Maybe fields, return lower as default
  if (!a) {
    return 1;
  }

  const compareUpTo = compareBuild ? 4 : 3;
  const pa = a.split(".");
  const pb = b.split(".");
  for (let i = 0; i < compareUpTo; i++) {
    const na = Number(pa[i]);
    const nb = Number(pb[i]);
    if (na > nb) return 1;
    if (nb > na) return -1;
    if (!isNaN(na) && isNaN(nb)) return 1;
    if (isNaN(na) && !isNaN(nb)) return -1;
  }

  return 0;
};

export const cleanString = (value: string | null): string => {
  if (!value) {
    return "";
  }

  return value.trim().replace(/[^\w]/gi, "");
};

export const compareToIgnoreCase = (s1: string, s2: string): number =>
  simpleCollator.compare(s1, s2);

export const nameIsUnassigned = (name: string | undefined) => {
  return name?.toLowerCase().trim() === UNASSIGNED;
};

export const toPartialHiddenText = (
  value: string,
  visibleCharacterCount: number = 6,
  bulletCount: number = 8
): string => {
  const showPartialCharacterCount = Math.min(
    Math.floor(value.length / 2),
    Math.max(Math.floor(visibleCharacterCount / 2), 1)
  );
  const prefix = value.substr(0, showPartialCharacterCount);
  const bullets = "\u2022".repeat(Math.max(bulletCount, 0));
  const suffix = value.substr(value.length - showPartialCharacterCount);
  return `${prefix}${bullets}${suffix}`;
};
